import {
  Button,
  Combobox,
  ComboboxContent,
  ComboboxGroup,
  ComboboxItem,
  ComboboxSearchField,
  ComboboxTrigger,
  ComboboxTriggerLoading,
  ComboboxValue,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogDismiss,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  InputMask,
  masks,
} from '@barfacil/barfacil-ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import {
  CategoryOptionsResponse,
  UpdatePriceListProductBody,
} from '@/api/_schemas/management'
import { usePriceListCategoryOptions } from '@/app/panel/management/price-list/_hooks/use-category-options'
import { usePriceListProductOptions } from '@/app/panel/management/price-list/_hooks/use-product-options'
import { useUpdatePriceListProduct } from '@/app/panel/management/price-list/_hooks/use-update-price-list-product'
import {
  FormCreatePriceListProductInLine,
  FormCreatePriceListProductInLineSchema,
} from '@/app/panel/management/price-list/_schemas/update-price-list-product-in-line'
import { useFormErrorHandler } from '@/hooks/use-form-error-handler'
import {
  formatToNumber,
  removeAccents,
  resetBodyPointerEvents,
} from '@/lib/utils'

type UpdateProductInLineDialogProps = {
  formKey: keyof Omit<UpdatePriceListProductBody, 'imagem'>
  formValue: UpdatePriceListProductBody
  priceListId: number
  id: number
  isInactive: boolean
  isCombo: boolean
}

const formKeysIds = {
  produto: 'price-list-edit-product-combobox',
  categoria: 'price-list-edit-category-combobox',
  valor: 'price-list-edit-price',
}

export const UpdateProductInLineDialog = ({
  formKey,
  formValue,
  priceListId,
  id,
  isInactive,
  isCombo,
}: UpdateProductInLineDialogProps) => {
  const [open, setOpen] = useState(false)

  const [openComboboxCategory, setOpenComboboxCategory] = useState(false)

  const [categorySearch, setCategorySearch] = useState('')

  const [filteredCategories, setFilteredCategories] =
    useState<CategoryOptionsResponse>([])

  usePriceListProductOptions(priceListId)
  const { data: categoryOptions, isLoading: isLoadingCategoryOptions } =
    usePriceListCategoryOptions()

  const { mutation, formErrors } = useUpdatePriceListProduct()

  const form = useForm<FormCreatePriceListProductInLine>({
    resolver: zodResolver(FormCreatePriceListProductInLineSchema),
    defaultValues: {
      ...formValue,
      valor: parseFloat(formValue.valor).toFixed(2),
    },
  })

  useFormErrorHandler({
    form,
    formErrors,
    isError: mutation.isError,
    isSuccess: false,
  })

  function handleOnOpenChange(open: boolean) {
    if (open) {
      form.reset({
        ...formValue,
        valor: parseFloat(formValue.valor).toFixed(2),
      })
    } else {
      resetBodyPointerEvents()
    }

    setOpen(open)
  }

  function handleCategorySearchChange(
    event: React.ChangeEvent<HTMLInputElement>,
  ) {
    const value = event.currentTarget.value

    setCategorySearch(value)
    form.setValue('categoria', value)

    setFilteredCategories(
      categoryOptions?.filter(({ nome }) =>
        removeAccents(nome.toUpperCase()).includes(
          removeAccents(value.toUpperCase()),
        ),
      ) ?? [],
    )
  }

  function handleOnSelectCategory(value: string) {
    form.setValue('categoria', value)
  }

  function handleSubmit<T>(e: React.FormEvent<T>) {
    e.preventDefault()

    return form.handleSubmit((data) => {
      mutation.mutate(
        {
          ...data,
          priceListId,
          id,
          valor: formatToNumber(data.valor),
        },
        {
          onSuccess: () => setOpen(false),
        },
      )
    })()
  }

  return (
    <Dialog open={open} onOpenChange={handleOnOpenChange}>
      <DialogTrigger asChild>
        <Button
          className="pl-3 font-normal hover:text-sky-400 active:text-sky-800 disabled:bg-transparent"
          variant="ghost"
          type="button"
          disabled={isInactive}
        >
          {formKey === 'valor'
            ? masks.currency(Number(formValue[formKey]).toFixed(2))
            : formValue[formKey]}
        </Button>
      </DialogTrigger>

      <DialogContent className="w-full max-w-lg gap-8">
        <DialogHeader>
          <DialogTitle className="leading-normal">EDITAR PRODUTO</DialogTitle>
        </DialogHeader>

        <DialogDescription className="hidden" />

        <DialogBody className="line-clamp-1 p-1">
          <Form {...form}>
            <form className="flex flex-col gap-2" onSubmit={handleSubmit}>
              <FormField
                control={form.control}
                name="produto"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome</FormLabel>

                    <FormControl>
                      <Input
                        autoFocus
                        placeholder="Produto"
                        {...field}
                        disabled={mutation.isPending}
                      />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="categoria"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CATEGORIA</FormLabel>

                    <Combobox
                      open={openComboboxCategory}
                      onOpenChange={setOpenComboboxCategory}
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      focusSelected={false}
                      disabled={mutation.isPending}
                    >
                      <FormControl>
                        {isLoadingCategoryOptions ? (
                          <ComboboxTriggerLoading />
                        ) : (
                          <ComboboxTrigger
                            id={formKeysIds.categoria}
                            onBlur={() => setCategorySearch('')}
                          >
                            <ComboboxValue placeholder="Informe o nome da categoria">
                              {field.value}
                            </ComboboxValue>
                          </ComboboxTrigger>
                        )}
                      </FormControl>

                      <ComboboxContent>
                        <ComboboxSearchField
                          value={categorySearch}
                          autoComplete="off"
                          loading={isLoadingCategoryOptions}
                          onChange={handleCategorySearchChange}
                          onKeyDown={(e) => {
                            if (e.key !== 'Enter') return
                            setOpenComboboxCategory(false)
                          }}
                        />

                        <ComboboxGroup>
                          {(!categorySearch
                            ? categoryOptions
                            : filteredCategories
                          )?.map(({ cod_produto: id, nome }) => (
                            <ComboboxItem
                              key={id}
                              value={nome}
                              onClick={() => {
                                handleOnSelectCategory(nome)
                                setOpenComboboxCategory(false)
                              }}
                            >
                              {nome}
                            </ComboboxItem>
                          ))}
                        </ComboboxGroup>
                      </ComboboxContent>
                    </Combobox>

                    <FormMessage />
                  </FormItem>
                )}
              />

              {!isCombo && (
                <FormField
                  control={form.control}
                  name="valor"
                  render={({ field }) => (
                    <FormItem id={formKeysIds.valor}>
                      <FormLabel>VALOR</FormLabel>

                      <FormControl>
                        <InputMask
                          {...field}
                          placeholder="R$"
                          mask="currency"
                          disabled={mutation.isPending}
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <footer className="mt-2 flex flex-col gap-2 sm:ml-auto sm:flex-row">
                <Button
                  className="sm:w-32"
                  type="button"
                  variant="tertiary"
                  onClick={() => setOpen(false)}
                  disabled={mutation.isPending}
                >
                  CANCELAR
                </Button>

                <Button
                  className="sm:w-32"
                  type="submit"
                  disabled={mutation.isPending}
                  isLoading={mutation.isPending}
                >
                  SALVAR
                </Button>
              </footer>
            </form>
          </Form>
        </DialogBody>

        <DialogDismiss className="absolute right-6 top-6" />
      </DialogContent>
    </Dialog>
  )
}
